import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as fabric from "fabric";
import {
  Circle,
  Square,
  SquareUserRound,
  FlipHorizontal2,
  Webcam,
} from "lucide-react";
import useRecorderContext from "../context/useRecorderContext";
import styles from "./SelectionToolbar.module.css";

export interface SelectionToolbarProps {
  canvas: fabric.Canvas;
  webcamObjectId?: string;
  isRecordingRef: React.MutableRefObject<boolean>;
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  canvas,
  webcamObjectId = "webcam-frame",
  isRecordingRef,
}) => {
  const {
    roundedWebcam,
    setRoundedWebcam,
    mirroredWebcam,
    setMirroredWebcam,
    avatarWebcam,
    setAvatarWebcam,
    isUploading,
  } = useRecorderContext();

  // Always mounted - control visibility via CSS class
  const [visible, setVisible] = useState(false);

  const toolbarRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const isTransformingRef = useRef<boolean>(false);

  // Store positions to avoid (0,0) flash
  const lastPosRef = useRef<{ x: number; y: number }>({ x: -10000, y: -10000 });
  const pendingPosRef = useRef<{ x: number; y: number } | null>(null);

  const setTransform = useCallback((x: number, y: number) => {
    lastPosRef.current = { x, y };
    toolbarRef.current?.style.setProperty(
      "transform",
      `translate3d(${x}px, ${y}px, 0)`
    );
  }, []);

  /** Hide without unmounting (no flash on next show) */
  const hideInstant = useCallback(() => {
    setVisible(false);
  }, []);

  // Apply transform BEFORE paint when visibility changes
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;

    if (visible) {
      const p = pendingPosRef.current ?? lastPosRef.current;
      // Write the final transform BEFORE we allow paint
      el.style.transition = "none";
      setTransform(p.x, p.y);
      // Ensure style applied before painting
      void el.offsetWidth; // force reflow
      pendingPosRef.current = null;
    }
  }, [visible, setTransform]);

  const handleToggleRounded = useCallback(() => {
    setRoundedWebcam(!roundedWebcam);
  }, [roundedWebcam, setRoundedWebcam]);

  const handleToggleMirror = useCallback(() => {
    setMirroredWebcam(!mirroredWebcam);
  }, [mirroredWebcam, setMirroredWebcam]);

  const handleToggleAvatar = useCallback(() => {
    setAvatarWebcam(!avatarWebcam);
  }, [avatarWebcam, setAvatarWebcam]);

  const isWebcamObject = useCallback(
    (obj: fabric.Object): boolean => {
      const fabricObj = obj as any;
      const name = fabricObj.name || "";
      return name === webcamObjectId || name.includes("webcam");
    },
    [webcamObjectId]
  );

  const canShowToolbar = useCallback(() => {
    if (!canvas) return false;
    if (isRecordingRef.current) return false;
    if (isTransformingRef.current) return false;
    if (isUploading) return false;

    const active = canvas.getActiveObjects();
    return active.length === 1 && isWebcamObject(active[0]);
  }, [canvas, isWebcamObject]);

  /** Schedule a show with a final transform, no intermediate paint */
  const showAt = useCallback(
    (x: number, y: number) => {
      if (!canShowToolbar()) return; // <- hard gate
      pendingPosRef.current = { x, y };
      setVisible(true);
    },
    [canShowToolbar]
  );

  const topCenterFromControls = useCallback(
    (obj: fabric.Object): { x: number; y: number; visible: boolean } => {
      if (!canvas) return { x: 0, y: 0, visible: false };

      try {
        const wrapper = (canvas.upperCanvasEl as HTMLCanvasElement)
          .parentElement as HTMLElement;
        const canvasEl = canvas.upperCanvasEl as HTMLCanvasElement;
        if (!wrapper || !canvasEl) return { x: 0, y: 0, visible: false };

        // Always refresh object coordinates before reading oCoords/aCoords
        obj.setCoords();

        // Prefer aCoords (Fabric v5), fallback to oCoords (v4)
        const anyObj = obj as any;
        const coords = anyObj.aCoords ?? anyObj.oCoords;
        if (!coords) return { x: 0, y: 0, visible: false };

        // true top-center in viewport/backing-store pixels
        const mt = coords.mt ?? {
          x: (coords.tl.x + coords.tr.x) / 2,
          y: (coords.tl.y + coords.tr.y) / 2,
        };

        // scale factors: backing-store px -> CSS px
        const scaleX = canvasEl.clientWidth / canvasEl.width;
        const scaleY = canvasEl.clientHeight / canvasEl.height;

        // coords in CSS px RELATIVE TO the canvas element
        const xCssInCanvas = mt.x * scaleX;
        const yCssInCanvas = mt.y * scaleY;

        // Offset of canvas inside wrapper (wrapper-local)
        const originX = canvasEl.offsetLeft;
        const originY = canvasEl.offsetTop;

        const TOOLBAR_W = toolbarRef.current?.offsetWidth ?? 48;
        const TOOLBAR_H = toolbarRef.current?.offsetHeight ?? 48;
        const GAP = 16;

        // Final wrapper-local coords (no page scroll math)
        let x = Math.round(originX + xCssInCanvas - TOOLBAR_W / 2);
        let y = Math.round(originY + yCssInCanvas - TOOLBAR_H - GAP);

        // Clamp horizontally inside wrapper
        const maxX = wrapper.clientWidth - TOOLBAR_W;
        if (x < 0) x = 0;
        if (x > maxX) x = maxX;
        if (y < 0) y = 0; // keep visible when very close to top edge

        return { x, y, visible: true };
      } catch (error) {
        console.error("Error calculating toolbar position:", error);
        return { x: 0, y: 0, visible: false };
      }
    },
    [canvas]
  );

  const updateToolbarPosition = useCallback(() => {
    if (!canShowToolbar()) {
      // <- replace all early checks with this
      hideInstant();
      return;
    }
    const obj = canvas!.getActiveObjects()[0];
    const p = topCenterFromControls(obj);
    if (!visible) showAt(p.x, p.y);
    else setTransform(p.x, p.y);
  }, [
    canvas,
    canShowToolbar,
    topCenterFromControls,
    visible,
    showAt,
    hideInstant,
    setTransform,
  ]);

  const scheduleUpdate = useCallback(() => {
    if (isRecordingRef.current) return; // <- new
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateToolbarPosition();
    });
  }, [updateToolbarPosition]);

  const handleSelectionCreated = useCallback(
    (e: any) => {
      const selection = e.selected || [];
      if (selection.length !== 1 || !isWebcamObject(selection[0])) {
        hideInstant();
        return;
      }

      // Use rAF to ensure Fabric has applied selection transforms
      requestAnimationFrame(() => {
        const a = canvas.getActiveObject();
        a?.setCoords?.();
        updateToolbarPosition();
      });
    },
    [isWebcamObject, updateToolbarPosition, hideInstant, canvas]
  );

  const handleSelectionUpdated = useCallback(
    (e: any) => {
      handleSelectionCreated(e);
    },
    [handleSelectionCreated]
  );

  const handleSelectionCleared = useCallback(() => {
    hideInstant();
  }, [hideInstant]);

  const handleObjectMoving = useCallback(
    (e: any) => {
      const obj = e.target;
      if (!obj || !isWebcamObject(obj)) return;
      isTransformingRef.current = true;
      hideInstant();
    },
    [isWebcamObject, hideInstant]
  );

  const handleObjectScaling = useCallback(
    (e: any) => {
      const obj = e.target;
      if (!obj || !isWebcamObject(obj)) return;
      isTransformingRef.current = true;
      hideInstant();
    },
    [isWebcamObject, hideInstant]
  );

  const handleObjectRotating = useCallback(
    (e: any) => {
      const obj = e.target;
      if (!obj || !isWebcamObject(obj)) return;
      isTransformingRef.current = true;
      hideInstant();
    },
    [isWebcamObject, hideInstant]
  );

  const handleObjectModified = useCallback(
    (e: any) => {
      const obj = e.target;
      if (!obj || !isWebcamObject(obj)) return;

      isTransformingRef.current = false;
      obj.setCoords?.();

      if (!canShowToolbar()) {
        // <- new
        hideInstant();
        return;
      }

      const p = topCenterFromControls(obj);
      showAt(p.x, p.y);
    },
    [isWebcamObject, topCenterFromControls, showAt, canShowToolbar, hideInstant]
  );

  const handleMouseUp = useCallback(
    (e: any) => {
      const active = canvas?.getActiveObjects();
      if (!(active?.length === 1 && isWebcamObject(active[0]))) return;

      isTransformingRef.current = false;
      active[0].setCoords?.();

      if (!canShowToolbar()) {
        // <- new
        hideInstant();
        return;
      }

      const p = topCenterFromControls(active[0]);
      showAt(p.x, p.y);
    },
    [
      canvas,
      isWebcamObject,
      topCenterFromControls,
      showAt,
      canShowToolbar,
      hideInstant,
    ]
  );

  const handleCanvasZoom = useCallback(() => {
    scheduleUpdate();
  }, [scheduleUpdate]);

  const handleAfterRender = useCallback(() => {
    if (visible) {
      scheduleUpdate();
    }
  }, [visible, scheduleUpdate]);

  const handleWindowResize = useCallback(() => {
    scheduleUpdate();
  }, [scheduleUpdate]);

  const handleCanvasMouseUp = useCallback(
    (e: any) => {
      handleMouseUp(e);
    },
    [handleMouseUp]
  );

  // Setup event listeners
  useEffect(() => {
    if (!canvas) return;

    const listeners = [
      { event: "selection:created", handler: handleSelectionCreated },
      { event: "selection:updated", handler: handleSelectionUpdated },
      { event: "selection:cleared", handler: handleSelectionCleared },
      { event: "object:moving", handler: handleObjectMoving },
      { event: "object:scaling", handler: handleObjectScaling },
      { event: "object:rotating", handler: handleObjectRotating },
      { event: "object:modified", handler: handleObjectModified },
      { event: "mouse:up", handler: handleCanvasMouseUp },
      { event: "mouse:wheel", handler: handleCanvasZoom },
      { event: "after:render", handler: handleAfterRender },
    ];

    // Attach all listeners
    listeners.forEach(({ event, handler }) => {
      canvas.on(event as any, handler);
    });

    // Add window resize listener
    window.addEventListener("resize", handleWindowResize);

    return () => {
      // Remove all canvas listeners
      listeners.forEach(({ event, handler }) => {
        canvas.off(event as any, handler);
      });

      // Remove window resize listener
      window.removeEventListener("resize", handleWindowResize);

      // Cancel any pending rAF
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [
    canvas,
    handleSelectionCreated,
    handleSelectionUpdated,
    handleSelectionCleared,
    handleObjectMoving,
    handleObjectScaling,
    handleObjectRotating,
    handleObjectModified,
    handleCanvasZoom,
    handleAfterRender,
    handleWindowResize,
    handleCanvasMouseUp,
  ]);

  // Update toolbar visibility when recording state changes
  useEffect(() => {
    if (isRecordingRef.current) {
      // kill any scheduled updates and hide now
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      hideInstant();
    } else {
      // recording ended: optionally show if selection is valid
      if (!visible && canShowToolbar()) {
        const obj = canvas?.getActiveObjects()?.[0];
        if (obj) {
          const p = topCenterFromControls(obj);
          // Write transform in layout effect before showing (no flash)
          pendingPosRef.current = { x: p.x, y: p.y };
          setVisible(true);
        }
      }
    }
  }, [
    isRecordingRef.current,
    canvas,
    visible,
    canShowToolbar,
    hideInstant,
    topCenterFromControls,
  ]);

  // Always render - control visibility via CSS class
  return (
    <div
      ref={toolbarRef}
      className={`${styles.toolbar} ${
        visible && !isTransformingRef.current ? styles["toolbar--visible"] : ""
      }`}
      aria-hidden={!visible}
    >
      <button
        className={styles.toggleButton}
        onClick={handleToggleRounded}
        aria-label="Toggle rounded webcam"
        type="button"
      >
        {roundedWebcam ? (
          <Square className={`${styles.icon} ${styles.square}`} />
        ) : (
          <Circle className={`${styles.icon} ${styles.rounded}`} />
        )}
      </button>
      <button
        className={styles.toggleButton}
        aria-label="Toggle webcam/user avatar"
        type="button"
        onClick={handleToggleAvatar}
      >
        {avatarWebcam ? (
          <Webcam className={styles.icon} />
        ) : (
          <SquareUserRound className={styles.icon} />
        )}
      </button>
      <button
        className={styles.toggleButton}
        disabled={avatarWebcam}
        aria-label="Mirror webcam horizontally"
        type="button"
        style={
          avatarWebcam ? { opacity: 0.32, cursor: "not-allowed" } : undefined
        }
        onClick={handleToggleMirror}
      >
        <FlipHorizontal2 className={styles.icon} />
      </button>
    </div>
  );
};

export default SelectionToolbar;
